<template>
  <div v-if="show" class="mt-4">
    <v-layout v-if="loading.history" row justify-center pa-4>
      <v-progress-circular indeterminate />
    </v-layout>
    <v-alert
      :value="isError"
      color="warning-text"
      text
      class="warning text-center"
      type="warning"
    >
      {{ $t('activity_reservation.error.history_error') }}
    </v-alert>
    <v-layout column mb-6 style="padding-left: 17px; padding-right: 17px;">
      <v-card flat v-for="(item, index) in activityReservationLogs" :key="index">
        <template>
          <div
            :class="`timeline-alert timeline-alert--${getStatus(item)}`"
          >
            <v-container class="pl-0">
              <div class="d-flex flex-nowrap justify-space-between">
                <div class="flex-none mr-2">
                  <v-icon class="material-symbols-rounded" :color="`${ activityReservationColorsByStatus[getStatus(item)].dot }`">
                    {{getIcon(item)}}
                  </v-icon>
                </div>
                <v-layout column>
                  <v-chip :class="`timeline-status-chip date-chip--${getStatus(item)}_dark`">{{ getTitle(item) }}</v-chip>
                  <v-layout row align-center class="ma-0 gap-1">
                    <v-chip small label :class="`date-chip date-chip--${getStatus(item)}`">
                      {{ displayFormatDate(moment(item.created).local(),$vuetify.breakpoint.smAndDown ? 'sm': 'xl') }} | {{ moment(item.created).local().format(getTimeFormat()) }}
                    </v-chip>
                    <v-chip 
                      small
                      label
                      v-if="isFailedPayment(item) || isSuccessfulPayment(item) || isSuccessRefund(item)"
                      :class="`date-chip date-chip--${getStatus(item)}_dark mr-1`"
                    >
                      {{ getPaymentStatus(item) }}
                    </v-chip>
                    <v-chip
                      v-if="isFailedPayment(item) || isSuccessfulPayment(item) || isSuccessRefund(item)"
                      label
                      small
                      :class="`date-chip date-chip--${getStatus(item)}_dark`"
                    >
                      <span class="white--text font-weight-700 pr-1">
                        {{ getPriceString({ amountAndCurrency: totalCalculated }) }}
                      </span>
                    </v-chip>
                    <span class="ml-1" v-if="getStatus(item) === ATTENDED">{{ $t(`reservations.finished.${reservation.type}`) }}</span>
                  </v-layout>
                </v-layout>
              </div>
              <!-- Current requested date and time -->
              <v-layout class="pl-7 pt-2" v-if="item.current && !item.previous || item.previous && (item.previous.date !== item.current.date || item.previous.time !== item.current.time)">
                <span>{{ $t(`reservations.requested_for.${reservation.type}`)}}</span>
                <span
                  class="px-1"
                >
                  {{ displayFormatDate(moment(item.current.date).utc(0),$vuetify.breakpoint.smAndDown ? 'sm': 'xl') }}
                </span>
                <span>
                  {{ moment.utc(item.current.time).format(getTimeFormat()) }}
                </span>
              </v-layout>
              <div class="d-flex pl-7">
                  <div class="d-flex flex-column">
                    <div v-if="item.current && item.current.reason" class="d-flex align-start mt-2">
                      <span>
                        {{ item.current.reason }}
                      </span>
                    </div>
                    <div
                      v-if="item.initiator === 'GUEST' && item.webappUserCredentialId && !isFailedPayment(item)"
                      class="d-flex align-center mt-2"
                    >
                      <span>
                        {{ getCredentialEmail(item.webappUserCredentialId) }}
                      </span>
                    </div>
                  </div>
              </div>
              <v-layout row justify-start class="pl-7 mx-0 my-0">
                <!-- Payment info -->
                <div class="pt-2 mb-1" v-if="isFailedPayment(item) || isSuccessfulPayment(item) || isSuccessRefund(item)">
                  <div class="pb-1">
                    <span v-text="paymentMethodName(item.paymentGatewayId)" /> | <span v-text="`ID: ${item.paymentTransactionId}`"/>
                  </div>
                  <div v-if="getCardInfo(item)">
                    {{ $t('reservations.card_used') }}: *** {{ getCardInfo(item) }}
                  </div>
                </div>
              </v-layout>
              <v-layout row justify-end class="pl-7 mx-0 my-0">
                <!-- Previous reservation button -->
                <v-btn 
                  v-if="item.current && item.current.status === ACCEPTED_BY_ACTIVITY_MODIFICATION" 
                  small
                  class="reservation-button--ACCEPTED_BY_ACTIVITY_MODIFICATION mt-2"
                  @click="$emit('goToReservation', item.previous.related_activity_reservation_id)"
                >
                  {{ $t(`reservations.go_to_previous.${reservation.type}`) }}
                  <v-icon x-small class="material-symbols-rounded ml-1">arrow_forward</v-icon>
                </v-btn>
                <!-- Updated reservation button -->
                <v-btn 
                  v-if="item.current && item.current.status === CANCELLED_BY_ACTIVITY_MODIFICATION" 
                  outlined 
                  small
                  class="reservation-button--CANCELLED_BY_ACTIVITY_MODIFICATION mt-2"
                  @click="$emit('goToReservation', item.current.related_activity_reservation_id)"
                >
                  {{ $t(`activity_reservation.actions.go_to_updated.${reservation.type}`) }}
                  <v-icon x-small class="material-symbols-rounded ml-1">arrow_forward</v-icon>
                </v-btn>
                <!-- Retry payment button -->
                <v-btn
                  v-if="item.showRetry && paymentMethods.length"
                  class="reservation-button--PENDING_PAYMENT"
                  small
                  :loading="loading.retryPayment"
                  @click="retryPayment()"
                >
                  {{ $t('activity_reservation.error.retry') }}
                  <v-icon x-small class="material-symbols-rounded ml-1">arrow_forward</v-icon>
                </v-btn>
              </v-layout>
            </v-container>
          </div>
        </template>
      </v-card>
    </v-layout>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import moment from 'moment'

import { activityReservationColorsByStatus, activityReservationColorsByStatusDark, activityReservationStatus, reservationTypes, activityReservationLogStatus, logTags } from '@/config'
import { ActivityReservationsRequests } from '@/services/activityReservations.requests'
import { getTimeFormat } from '@/lib/helpers/HoursFormatter'
import { displayFormatDate } from '@/lib/helpers/DateUtils'
import { getTextValue } from '@/lib/helpers/LocalizedText'
import routeNames from '@/router/routes'
import activityMixins from '@/mixins/Activity'

const { 
  ACCEPTED, 
  PENDING, 
  PENDING_MODIFICATION, 
  ABSENT, 
  CANCELLED, 
  ATTENDED,
  UPDATED,
  ACCEPTED_BY_ACTIVITY_MODIFICATION,
  CANCELLED_BY_ACTIVITY_MODIFICATION,
  PENDING_PAYMENT,
} = activityReservationStatus

const activityReservationsService = new ActivityReservationsRequests()

export default {
  
  mixins: [
    activityMixins,
  ],
  props: {
    show: Boolean,
    item: Object,
  },

  data () {
    return {
      moment,
      activityReservationLogs: [],
      activityReservationColorsByStatus,
      activityReservationColorsByStatusDark,
      activityReservationStatus,
      getTimeFormat,
      ABSENT,
      loading: {
        history: true,
        transactions: true,
        retryPayment: false,
      },
      isError: false,
      wasRefunded: null,
      CANCELLED,
      displayFormatDate,
      ACCEPTED_BY_ACTIVITY_MODIFICATION,
      CANCELLED_BY_ACTIVITY_MODIFICATION,
      ATTENDED,
      activityReservationLogStatus,
    }
  },
  methods: {
    getTitle (item) {
      if (item.current &&
        item.current.status === ACCEPTED &&
        (item.previous === null ||
          item.previous.status === PENDING)
      ) {
        return this.$te(`reservations.timeline_status.confirmed.${ this.reservation.type }`) ?
          this.$t(`reservations.timeline_status.confirmed.${ this.reservation.type }`) :
          this.$t('reservations.timeline_status.confirmed')
      } else if (item.current &&
        item.current.status === ACCEPTED &&
        item.previous !== null
      ) {
        return this.$te(`reservations.timeline_status.updated.${ this.reservation.type }`) ?
          this.$t(`reservations.timeline_status.updated.${ this.reservation.type }`) :
          this.$t('reservations.timeline_status.updated')
      } else if (item.current &&
        item.current.status === PENDING &&
        item.previous === null
      ) {
        return this.$t('reservations.timeline_status.pending_approval')
      } else if (item.current &&
        item.current.status === PENDING_MODIFICATION
      ) {
        return this.$t('reservations.timeline_status.requested_modification')
      } else if (item.current && (item.current.status === ACCEPTED_BY_ACTIVITY_MODIFICATION ||
        item.current.status === CANCELLED_BY_ACTIVITY_MODIFICATION) ) 
      {
        return this.$t(`reservations.timeline_status.${ item.current.status.toLowerCase() }.${ this.reservation.type }`)
      } else if (item.tag === logTags.ACTIVITY_RESERVATIONS_PAYMENT_ATTEMPT) {
        return this.$t('reservations.timeline_status.payment')
      } else {
        return this.isRoomService && this.$te('reservations.order_status.' + item.current.status)
          ? this.$t('reservations.order_status.' + item.current.status)
          : this.$t('reservations.status.' + item.current.status)
      }
    },
    getIcon (item) {
      if (item.current &&
        item.current.status === ACCEPTED &&
        (item.previous === null ||
          item.previous.status === PENDING)
      ) {
        return 'group'
      } else if (item.current &&
        item.current.status === ACCEPTED &&
        item.previous !== null
      ) {
        return 'badge'
      } else if (item.current &&
        item.current.status === PENDING &&
        item.previous === null
      ) {
        return 'group'
      } else if (item.current &&
        item.current.status === PENDING_MODIFICATION
      ) {
        return 'group'
      } else if (item.current && (item.current.status === ACCEPTED_BY_ACTIVITY_MODIFICATION ||
        item.current.status === CANCELLED_BY_ACTIVITY_MODIFICATION) ) 
      {
        return 'badge'
      } else if (item.tag === logTags.ACTIVITY_RESERVATIONS_PAYMENT_ATTEMPT) {
        return 'credit_card'
      } else if (item.current && (item.current.status === ATTENDED)) {
        return 'change_circle'
      } else {
        return 'group'
      }
    },
    getPaymentStatus (item) {
      if (item.tag === logTags.ACTIVITY_RESERVATIONS_PAYMENT_ATTEMPT) {
        if (this.$te(`reservations.log_status.${ item.success }`)) {
          return this.$t(`reservations.log_status.${ item.success }`)
        } else {
          return this.$t('reservations.log_status.REFUSED')
        }
      }
      return null
    },
    getCardInfo (item) {
      if (item.order && item.order.transactions && item.order.transactions[0].source) {
        return item.order.transactions[0].source.last4
      }
      return null
    },
    getHistory () {
      this.loading.history = true
      this.activityReservationLogs = []
      this.isError = false
      activityReservationsService
        .getHistory(this.reservation.id)
        .then((result) => {
          result = result.map((status) => {
            status.updated = status.current && status.current.status === activityReservationStatus.ACCEPTED && 
                              (!status.previous || status.previous.date !== status.current.date || 
                              status.previous.time !== status.current.time)
            return status
          })

          const completedStatus = [ACCEPTED, PENDING, PENDING_MODIFICATION, activityReservationLogStatus.PAYMENT_ATTEMPT, PENDING_PAYMENT]
          const isCompleted = result[0].current && !completedStatus.includes(result[0].current.status)

          this.activityReservationLogs = result

          const logsPaymentAttempt = this.activityReservationLogs
            .filter(({ tag }) => {
              return tag && tag === logTags.ACTIVITY_RESERVATIONS_PAYMENT_ATTEMPT
            })

          if (logsPaymentAttempt.length) {
            const lastLogUnpaidPaymentAttempt = logsPaymentAttempt
              .sort((a, b) => {
                return moment(a.created) - moment(b.created)
              })
              .findLast(({ success }) => {
                return success !== activityReservationLogStatus.SUCCESS
              })

            const isPaid = logsPaymentAttempt.some(({ success }) => success === activityReservationLogStatus.SUCCESS)

            if (!isCompleted && lastLogUnpaidPaymentAttempt && this.item.reservation.status !== ACCEPTED) lastLogUnpaidPaymentAttempt.showRetry = !isPaid
          } else {
            const logPendingPayment = this.activityReservationLogs.find(({ current }) => {
              return current && current.status === PENDING_PAYMENT
            })

            if (logPendingPayment && this.item.reservation.status !== ACCEPTED) logPendingPayment.showRetry = true
          }
        })
        .catch((error) => {
          this.isError = true
        })
        .finally(() => {
          this.loading.history = false
        })
    },
    getCredentialEmail (credentialId) {
      const webappUserCredential = this.webappUserCredentials.find((webappUserCredential) => webappUserCredential.id === credentialId)
      return webappUserCredential ? webappUserCredential.email : null
    },
    getStatus (item) {
      if (item.current && item.current.status === ACCEPTED && item.previous &&
          (item.previous.date !== item.current.date || item.previous.time !== item.current.time)) {
        return UPDATED
      }
      // Use PENDING_PAYMENT or ACCEPTED colors when log is PAYMENT_ATTEMPT
      if (item.tag === logTags.ACTIVITY_RESERVATIONS_PAYMENT_ATTEMPT) {
        // If was paid or refunded
        if ([activityReservationLogStatus.SUCCESS, activityReservationLogStatus.REFUNDED].includes(item.success)) {
          return ACCEPTED
        }

        return PENDING_PAYMENT
      }
      return item.current.status
    },

    isFailedPayment (item) {
      return item.tag === logTags.ACTIVITY_RESERVATIONS_PAYMENT_ATTEMPT && [activityReservationLogStatus.BLACKLISTED, activityReservationLogStatus.REFUSED].includes(item.success)
    },

    isSuccessfulPayment (item) {
      return item.tag === logTags.ACTIVITY_RESERVATIONS_PAYMENT_ATTEMPT && item.success === activityReservationLogStatus.SUCCESS
    },
    
    isSuccessRefund (item) {
      return item.tag === logTags.ACTIVITY_RESERVATIONS_PAYMENT_ATTEMPT && item.success === activityReservationLogStatus.REFUNDED
    },

    paymentMethodName (paymentGatewayId) {
      if (!this.activity || !this.activity.paymentMethodList.length) return this.$t('error.no_data')

      const paymentMethod = this.activity.paymentMethodList.find(({ id }) => id === paymentGatewayId)

      return getTextValue(paymentMethod.name)
    },

    async retryPayment () {
      if (this.paymentMethods.length === 1) {
        this.loading.retryPayment = true

        const params = {
          activityReservationId: this.reservation.id,
          paymentServiceId: this.paymentMethods[0].id !== 'roomCharge' ? this.paymentMethods[0].id : null,
        }
        await activityReservationsService.retryPayment(params.activityReservationId, params.paymentServiceId)
          .then((data) => {
            if (data.redirectUrl) {
              this.$router.push({ name: routeNames.reservationPayment.name, params: { activityId: this.reservation.activityId, redirectUrl: data.redirectUrl, ...params } })
            } else {
              this.$router.push({ name: routeNames.reservationComplete.name, params: { activityId: this.reservation.activityId, activityReservationId: this.reservation.id } })
            }
          })
          .catch((error) => {
            this.$store.commit('state/setError', error)
            this.$router.push({ name: routeNames.reservationError.name, params: { activityId: this.reservation.activityId } })
          })

        this.loading.retryPayment = false
      } else {
        this.$router.push({ name: routeNames.paymentMethods.name, params: { activityReservationId: this.reservation.id} })
      }
    },

    calculateTotal (orderLines) {
      let totals = {}
      orderLines.forEach( (orderLine) => {
        if(!orderLine.includedOnParentLine) {
          orderLine.price.forEach( item => {
            if(!totals[item.currency.name]) {
              totals[item.currency.name] = {
                amount: item.amount * orderLine.quantity,
                currency: item.currency,
              }
            }else{
              totals[item.currency.name].amount += item.amount * orderLine.quantity
            }
          })
        }
      })
      return Object.values(totals)[0]
    },
  },
  computed: {
    ...mapState({
      activity: (state) => state.activities.extendedActivity,
    }),
    ...mapGetters({
      getPriceString: 'me/getPriceString',
      accounts: 'me/accounts',
      authCredential: 'auth/authCredential',
      webappUserCredentials: 'me/webappUserCredentials',
    }),
    paymentMethods () {
      return this.allPaymentMethods.filter(({ enabled }) => enabled)
    },
    reservation () {
      return this.item.reservation
    },
    order () {
      return this.item.order
    },
    isRoomService () {
      return this.reservation.type === reservationTypes.ROOM_SERVICE
    },

    orderLinesToCalculate () {
      return this.item.pendingOrder && this.item.pendingOrder.orderLines ?
        this.item.pendingOrder.orderLines :
        this.item.order && this.item.order.orderLines ?
          this.item.order.orderLines : []
    },
    totalCalculated () {
      return this.calculateTotal(this.orderLinesToCalculate)
    },
  },
  created () {
    this.$store.dispatch('activities/getActivity', { id: this.item.activity.id }).catch(() => {})
    if (this.order) {
      this.$store.dispatch('me/getBookings').then(() => {
        this.$store.dispatch('transactions/list', {
          save: true,
          limit: 50,
          offset: 0,
          accountIds: this.accounts.map(({ id }) => id),
          guestId: this.order.guestId,
          stationId: this.order.stationId,
          type: 'BOOKING',
          currencyType: 'LEGAL',
          createdSince: this.order.createdAt,
        }).then(({ list }) => {
          const transaction = list[0] && list[0].transaction
          this.wasRefunded = transaction && transaction.amount && transaction.relatedTransactionId
        }).finally(() =>
          this.loading.transactions = false
        )
      }).catch(() => {
        this.loading.transactions = false
      })
    } else {
      this.loading.transactions = false
    }
  },
  mounted () {
    Object.keys(activityReservationColorsByStatusDark).forEach(status => {
      document.documentElement.style.setProperty(`--${ status }-text-color`, activityReservationColorsByStatus[status].text)
      document.documentElement.style.setProperty(`--${ status }-background-color`, activityReservationColorsByStatus[status].background)
      document.documentElement.style.setProperty(`--${ status }-dot-color`, activityReservationColorsByStatus[status].dot)
      document.documentElement.style.setProperty(`--${ status }-text-color-dark`, activityReservationColorsByStatusDark[status].text)
      document.documentElement.style.setProperty(`--${ status }-background-color-dark`, activityReservationColorsByStatusDark[status].background)
    })

    if (this.show) this.getHistory()
  },
  watch: {
    show (value) {
      if (value) {
        this.getHistory()
      }
    },
    activityReservationLogs () {
      if (this.activityReservationLogs.find(({ current }) => {
        return current && current.status === ACCEPTED_BY_ACTIVITY_MODIFICATION
      })) {
        this.$emit('createdByModification')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.v-card {
  border-radius: 10px 0 10px 10px;
  margin-top: calc(18px + 23px);
}
.timeline-alert {
  position: relative;
  height: 100%;
  padding-left: 32px;
  padding-right: 32px;
  padding-top: 18px;
  padding-bottom: 18px;
  border-radius: 10px;
  background: var(--v-grey-3-base);
  color: var(--v-grey-7-base);
  font-family: 'Mukta';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 17px;
  &::before {
    background: var('--FINISHED-background-color');
    border-radius: 10px 0 0 10px;
    position: absolute;
    content: "";
    height: 100%;
    width: 10px;
    top: 0;
    left: 0;
  }
  &:not(:last-child) {
    margin-bottom: 25px;
  }
  .v-icon {
    font-size: 18px;
  }
}
.timeline-status-chip {
  border-radius: 10px 10px 0 0 !important;
  max-height: 23px;
  position: absolute;
  top: -23px;
  right: 0;
  font-size: 15px !important;
  font-weight: 500 !important;
  line-height: 28px !important;
  letter-spacing: 0.5px !important;
}
.reservation-button {
  margin-right: -10px !important;
  &--ACCEPTED_BY_ACTIVITY_MODIFICATION {
    margin-right: -20px !important;
    text-transform: none !important;
    font-family: 'Mukta Mahee';
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    letter-spacing: 0.4px;
    color: var(--ACCEPTED_BY_ACTIVITY_MODIFICATION-text-color-dark) !important;
    background-color: var(--ACCEPTED_BY_ACTIVITY_MODIFICATION-background-color-dark) !important;
  }
  &--CANCELLED_BY_ACTIVITY_MODIFICATION {
    margin-right: -20px !important;
    text-transform: none !important;
    font-family: 'Mukta Mahee';
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    letter-spacing: 0.4px;
    line-height: normal;
    color: var(--CANCELLED_BY_ACTIVITY_MODIFICATION-text-color-dark) !important;
    background-color: var(--CANCELLED_BY_ACTIVITY_MODIFICATION-background-color-dark) !important;
  }
  &--PENDING_PAYMENT {
    margin-right: -20px !important;
    text-transform: none !important;
    font-family: 'Mukta Mahee';
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    letter-spacing: 0.4px;
    line-height: normal;
    color: var(--PENDING_PAYMENT-text-color-dark) !important;
    background-color: var(--PENDING_PAYMENT-background-color-dark) !important;
  }
}
.date-chip {
  &.v-chip--label {
    border-radius: 5px !important; 
    max-height: 18px;
    padding: 0 8px;
    .v-chip__content {     
      text-align: center;
      font-family: 'Mukta Mahee';
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 16px; /* 133.333% */
      letter-spacing: 0.4px;
    }
  }
  &--UPDATED {
    color: var(--UPDATED-text-color) !important;
    background-color: var(--UPDATED-background-color) !important;
    &_dark {
      color: var(--UPDATED-text-color-dark) !important;
      background-color: var(--UPDATED-background-color-dark) !important;
    }
  }
  &--ABSENT {
    color: var(--ABSENT-text-color) !important;
    background-color: var(--ABSENT-background-color) !important;
    &_dark {
      color: var(--ABSENT-text-color-dark) !important;
      background-color: var(--ABSENT-background-color-dark) !important;
    }
  }
  &--REJECTED {
    color: var(--REJECTED-text-color) !important;
    background-color: var(--REJECTED-background-color) !important;
    &_dark {
      color: var(--REJECTED-text-color-dark) !important;
      background-color: var(--REJECTED-background-color-dark) !important;
    }
  }
  &--CANCELLED {
    color: var(--CANCELLED-text-color) !important;
    background-color: var(--CANCELLED-background-color) !important;
    &_dark {
      color: var(--CANCELLED-text-color-dark) !important;
      background-color: var(--CANCELLED-background-color-dark) !important;
    }
  }
  &--ACCEPTED {
    color: var(--ACCEPTED-text-color) !important;
    background-color: var(--ACCEPTED-background-color) !important;
    &_dark {
      color: var(--ACCEPTED-text-color-dark) !important;
      background-color: var(--ACCEPTED-background-color-dark) !important;
    }
  }
  &--ATTENDED {
    color: var(--ATTENDED-text-color) !important;
    background-color: var(--ATTENDED-background-color) !important;
    &_dark {
      color: var(--ATTENDED-text-color-dark) !important;
      background-color: var(--ATTENDED-background-color-dark) !important;
    }
  }
  &--PENDING {
    color: var(--PENDING-text-color) !important;
    background-color: var(--PENDING-background-color) !important;
    &_dark {
      color: var(--PENDING-text-color-dark) !important;
      background-color: var(--PENDING-background-color-dark) !important;
    }
  }
  &--PENDING_MODIFICATION {
    color: var(--PENDING_MODIFICATION-text-color) !important;
    background-color: var(--PENDING_MODIFICATION-background-color) !important;
    &_dark {
      color: var(--PENDING_MODIFICATION-text-color-dark) !important;
      background-color: var(--PENDING_MODIFICATION-background-color-dark) !important;
    }
  }
  &--PENDING_PAYMENT {
    color: var(--PENDING_PAYMENT-text-color) !important;
    background-color: var(--PENDING_PAYMENT-background-color) !important;
    &_dark {
      color: var(--PENDING_PAYMENT-text-color-dark) !important;
      background-color: var(--PENDING_PAYMENT-background-color-dark) !important;
    }
  }
  &--CANCELLED_BY_ACTIVITY_MODIFICATION {
    color: var(--CANCELLED_BY_ACTIVITY_MODIFICATION-text-color) !important;
    background-color: var(--CANCELLED_BY_ACTIVITY_MODIFICATION-background-color) !important;
    &_dark {
      color: var(--CANCELLED_BY_ACTIVITY_MODIFICATION-text-color-dark) !important;
      background-color: var(--CANCELLED_BY_ACTIVITY_MODIFICATION-background-color-dark) !important;
    }
  }
  &--ACCEPTED_BY_ACTIVITY_MODIFICATION {
    color: var(--ACCEPTED_BY_ACTIVITY_MODIFICATION-text-color) !important;
    background-color: var(--ACCEPTED_BY_ACTIVITY_MODIFICATION-background-color) !important;
    &_dark {
      color: var(--ACCEPTED_BY_ACTIVITY_MODIFICATION-text-color-dark) !important;
      background-color: var(--ACCEPTED_BY_ACTIVITY_MODIFICATION-background-color-dark) !important;
    }
  }
}

.timeline-alert--UPDATED {
  &::before {
    background: var(--UPDATED-dot-color);
  }
}

.timeline-alert--FINISHED {
  &::before {
    background: var(--FINISHED-dot-color);
  }
}

.timeline-alert--ABSENT {
  &::before {
    background: var(--ABSENT-dot-color);
  }
}

.timeline-alert--REJECTED {
  &::before {
    background: var(--REJECTED-dot-color);
  }
}

.timeline-alert--CANCELLED {
  &::before {
    background: var(--CANCELLED-dot-color);
  }
}

.timeline-alert--CANCELLED_BY_ACTIVITY_MODIFICATION {
  &::before {
    background: var(--CANCELLED_BY_ACTIVITY_MODIFICATION-dot-color);
  }
}

.timeline-alert--ACCEPTED_BY_ACTIVITY_MODIFICATION {
  &::before {
    background: var(--ACCEPTED_BY_ACTIVITY_MODIFICATION-dot-color);
  }
}

.timeline-alert--ACCEPTED {
  &::before {
    background: var(--ACCEPTED-dot-color);
  }
}

.timeline-alert--PENDING {
  &::before {
     background: var(--PENDING-dot-color);
  }
}

.timeline-alert--PENDING_MODIFICATION {
  &::before {
     background: var(--PENDING_MODIFICATION-dot-color);
  }
}

.timeline-alert--ATTENDED {
  &::before {
     background: var(--ATTENDED-dot-color);
  }
}
.timeline-alert--PENDING_PAYMENT {
  &::before {
    background: var(--PENDING_PAYMENT-dot-color);
  }
}

.width--60 {
  width: 60px;
}
</style>
